import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AdhocReportBody } from "../../../components/Modals/AdhocReport";
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import { DELETE_CONTACT } from "../../../graphql/mutation/Contact";
import {
  FETCH_CONTACT as fetchContact,
  FETCH_CONTACTS_LIST as fetchContactsList,
} from "../../../graphql/query/Contact";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import useUser from "../../../hooks/useUser";
import {
  hasSearchInput,
  useSearchSynopsis,
} from "../../../hooks/useSearchSynopsis";
import { clearSelectionState } from "../../../util/clearSelectionState";
import {
  getSearchAwareLabel,
  useModuleSearchLocation,
} from "../../../util/usePersistedFilterFormik";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";
import { getAppEnvironment } from "../../../config";
import routes from "../pages/Wizard/routes";
import { ExportContactsBody } from "./Actions/ExportContacts";
import { ExportEmailsBody } from "./Actions/ExportEmails";
import exportModule from "./Actions/ExportModule";
import { GenerateReportBody } from "./Actions/GenerateReport";
import { HandoverBody } from "./Actions/Handover";
import MergeContact from "./Actions/MergeContact";
import NewContact from "./Actions/NewContact";
import SubscribeModal from "./Actions/Subscribe";
import UpdateCRM from "./Actions/UpdateCRM";
import ContactSearch from "./ContactSearch";
import { buildContactNavigationTargetUrl } from "./searchResultNavigation";
import {
  contactNavigationStore,
  getContactSearchFromLocationSearch,
} from "./useContactFilters";

const SALESFORCE_BASE_URL =
  getAppEnvironment() === "production"
    ? "https://white-cube.lightning.force.com"
    : "https://white-cube--uatwc.sandbox.lightning.force.com";

const fetchContactsNavigation = fetchContactsList({});

const PrimaryNav = props => {
  const { formik } = props;
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isHandoverOpen, setIsHandoverOpen] = useState(false);
  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const rememberedSearchLocation = useModuleSearchLocation("contacts");
  // Links that drop the query string (e.g. to a related contact) fall back to
  // the remembered search so the module stays in its search results.
  const searchLocation = hasSearchInput(
    getContactSearchFromLocationSearch(location.search),
  )
    ? location.search
    : rememberedSearchLocation
      ? `?${rememberedSearchLocation}`
      : location.search;
  const search = getContactSearchFromLocationSearch(searchLocation);
  const navigate = useNavigate();

  const hasSearch = hasSearchInput(search);
  const { synopsis: searchSynopsis } = useSearchSynopsis(
    contactNavigationStore,
  );
  const { hasPermission } = useUser();
  const pathnames = location?.pathname.split("/");
  // get id from url
  const contactId = pathnames?.length > 2 ? pathnames[2] : null;
  const contactIdNumber = contactId ? Number(contactId) : null;
  // fetch contact details if id is available
  const { data: { contact: fetchedContact } = {} } = useQuery(
    fetchContact({
      name: true,
      salesForceId: true,
      salesUserId: true,
    }),
    {
      skip: !contactId,
      variables: { id: +contactId },
    },
  );
  const [
    deleteContact,
    {
      data: { deleteContact: { error: deleteContactError } = {} } = {},
      loading: deleteContactLoading,
      reset,
    },
  ] = useMutation(DELETE_CONTACT);
  const salesforceContactUrl = fetchedContact?.salesForceId
    ? `${SALESFORCE_BASE_URL}/lightning/r/Account/${fetchedContact.salesForceId}/view`
    : null;

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(searchLocation);
  const status = queryParams.get("status") || "all";
  const listContext = useMemo(
    () => ({ locationSearch: searchLocation }),
    [searchLocation],
  );
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildContactNavigationTargetUrl,
    dataKey: "contactsV2",
    fetchQuery: fetchContactsNavigation,
    listContext,
    navigate,
    navigationStore: contactNavigationStore,
    recordId: contactIdNumber,
    status,
  });
  const listId = +(
    queryParams.get("listId") ||
    (status === "selectListWorking" ? queryParams.get("workingListId") : null)
  );

  const STATUS_MAP = {
    all: "All Contacts",
    my: "My Contacts",
    searched: "Searched Contacts",
    selectListWorking: "Select List (Working)",
  };
  const statusLabel = STATUS_MAP[status] || "All Contacts";
  // With an active search the root links back to the search results;
  // the human-readable synopsis is shown as its tooltip.
  const rootLabel =
    hasSearch && ["all", "my"].includes(status)
      ? {
          label: getSearchAwareLabel(statusLabel, true),
          title: searchSynopsis
            ? `Contact Search ${searchSynopsis}`
            : undefined,
        }
      : statusLabel;
  const steps = [
    {
      label: rootLabel,
      onClick: () =>
        navigate(
          `/contacts${searchLocation || (status !== "all" ? `?status=${status}` : "")}`,
        ),
    },
    ...(fetchedContact
      ? [
          {
            label: fetchedContact?.name,
            onClick: () =>
              navigate(
                buildContactNavigationTargetUrl({
                  listContext,
                  recordId: fetchedContact?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildContactNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: fetchedContact?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  const modalProps = {
    body: ContactSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  const deleteModalProps = {
    title: deleteContactError ? "Error" : "Are you sure?",
    scale: "sm",
    description: deleteContactError
      ? deleteContactError
      : `You are about to permeantly delete the contact record for ${fetchedContact?.name}.`,
    closeOnBackdrop: true,
    renderBody: ({ closeModal }) => {
      const handleDelete = () => {
        deleteContact({
          variables: {
            input: { id: fetchedContact?.id },
          },
          update: (cache, { data }) => {
            const { deleteContact: { success } = {} } = data || {};
            if (success) {
              closeModal();
              cache.evict({
                id: cache.identify({
                  __typename: "Contact",
                  id: contactId,
                }),
              });
              navigate("/contacts");
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={deleteContactError ? "Close" : "Cancel"}
            action={deleteContactError ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
              reset();
            }}
          />
          {!deleteContactError && (
            <Button
              label={deleteContactLoading ? "Deleting" : "Delete"}
              disabled={deleteContactLoading}
              onClick={handleDelete}
            />
          )}
        </div>
      );
    },
  };

  const scopedTargets = scopeBulkActionTargets({
    entityId: contactId,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => clearSelectionState(formik),
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New contact",
        modalProps: {
          body: NewContact,
          ...defaultActionProps,
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Update CRM Info",
        modalProps: {
          body: UpdateCRM,
          ...defaultActionProps,
        },
      },
      {
        disabled:
          (!ids.length && !contactId) || !hasPermission("HANDOVER_CONTACT"),
        label: "Handover",
        modalProps: {
          body: HandoverBody,
          ...defaultActionProps,
          isHandoverOpen,
          onOpen: () => {
            setIsHandoverOpen(true);
          },
          onClose: () => {
            setIsHandoverOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Contact Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Contact",
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: (
          <>
            Reporting{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${!ids.length && !contactId ? "bg-gray-100 text-gray-400" : "bg-green-100 text-green-700"}`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Contact",
          onClose: () => {},
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Emails",
        modalProps: {
          body: ExportEmailsBody,
          ...defaultActionProps,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Export Contacts",
        modalProps: {
          body: ExportContactsBody,
          ...defaultActionProps,
        },
      },
      {
        disabled: !ids.length && !contactId,
        label: "Subscribe",
        modalProps: {
          body: SubscribeModal,
          ...defaultActionProps,
        },
      },
      ...(contactId
        ? [
            {
              label: "Open in Salesforce",
              disabled: !fetchedContact?.salesForceId,
              href: salesforceContactUrl,
              target: "_blank",
              rel: "noopener,noreferrer",
            },
            {
              disabled: !hasPermission("DELETE_CONTACT"),
              label: "Delete contact",
              modalProps: deleteModalProps,
            },
            {
              label: "Merge contact",
              modalProps: {
                body: MergeContact,
                ...defaultActionProps,
                contact: fetchedContact,
              },
            },
          ]
        : []),
    ],
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...modalProps}>
          <Button
            label="Contact Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} commandPalette />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
